// When a template switches on. Four rows of chips — any one within a row,
// every row that has a pick — with the result spelled out underneath, and the
// cargo definitions it reaches, so "which cargo gets these checks" is answered
// without copying the checks onto each definition.
import { getCargoTypeLabel, type CargoTypeValue } from '@my/tms-api';
import { cn } from '@my/ui';
import { definitionsReached, describeAppliesWhen, type Catalogue } from '../lib/conditions';
import { CARGO_PROPERTIES, PROPERTY_LABEL, type AppliesWhen, type CargoProperty } from '../lib/model';

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
}

export function AppliesWhenCard({
  value,
  onChange,
  catalogue,
}: {
  value: AppliesWhen;
  onChange: (next: AppliesWhen) => void;
  catalogue: Catalogue;
}) {
  const all = catalogue.cargoDefinitions;
  // With cargo types picked, only their definitions are worth offering — a
  // definition of another type could never match as well. Picked ones stay.
  const definitions = value.cargoTypes.length
    ? all.filter((d) => value.cargoTypes.includes(d.type) || value.cargoDefinitionIds.includes(d.id))
    : all;
  const reached = definitionsReached(value, catalogue);
  // A pick that is no longer offered (deactivated since) stays visible, so it
  // can still be taken off.
  const orphan = (ids: string[], known: { id: string }[]) =>
    ids.filter((id) => !known.some((k) => k.id === id)).map((id) => ({ value: id, label: 'Unavailable' }));

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">Applies when</h3>
        <span className="text-[11px] text-slate-400">Any one in a row · every row you use · nothing picked = any</span>
      </div>
      <div className="space-y-2 px-4 py-3">
        <ChipRow<CargoTypeValue>
          label="Cargo type"
          options={[...new Set([...catalogue.cargoTypesInUse, ...value.cargoTypes])].map((t) => ({
            value: t,
            label: getCargoTypeLabel(t),
          }))}
          selected={value.cargoTypes}
          onToggle={(t) => onChange({ ...value, cargoTypes: toggle(value.cargoTypes, t) })}
        />
        <ChipRow<string>
          label="Cargo definition"
          options={[
            ...definitions.map((d) => ({ value: d.id, label: d.name })),
            ...orphan(value.cargoDefinitionIds, all),
          ]}
          selected={value.cargoDefinitionIds}
          onToggle={(id) => onChange({ ...value, cargoDefinitionIds: toggle(value.cargoDefinitionIds, id) })}
        />
        <ChipRow<string>
          label="Service tag"
          mono
          options={[
            ...catalogue.serviceTags.map((t) => ({ value: t.id, label: t.name })),
            ...orphan(value.serviceTagIds, catalogue.serviceTags),
          ]}
          selected={value.serviceTagIds}
          onToggle={(id) => onChange({ ...value, serviceTagIds: toggle(value.serviceTagIds, id) })}
        />
        <ChipRow<CargoProperty>
          label="Cargo has"
          options={CARGO_PROPERTIES.map((p) => ({ value: p, label: PROPERTY_LABEL[p] }))}
          selected={value.properties}
          onToggle={(p) => onChange({ ...value, properties: toggle(value.properties, p) })}
        />

        <div className="rounded-md bg-sky-50 px-2.5 py-1.5 text-xs text-sky-900">
          <p>{describeAppliesWhen(value, catalogue)}</p>
          <p className="mt-0.5 text-[11px] text-sky-700">
            {all.length === 0
              ? 'No active cargo definitions yet.'
              : reached.length === all.length
                ? `Reaches all ${all.length} cargo definitions.`
                : reached.length === 0
                  ? 'Reaches none of your cargo definitions on its own — only orders tagged by hand.'
                  : `Reaches ${reached.length} of ${all.length} cargo definitions: ${reached.map((d) => d.name).join(', ')}.`}
          </p>
        </div>
      </div>
    </div>
  );
}

function ChipRow<T extends string>({
  label,
  options,
  selected,
  onToggle,
  mono = false,
}: {
  label: string;
  options: { value: T; label: string }[];
  selected: T[];
  onToggle: (value: T) => void;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
      <span className="w-28 shrink-0 pt-0.5 text-[11px] text-slate-500">{label}</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
        {options.map((o) => {
          const on = selected.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={on}
              onClick={() => onToggle(o.value)}
              className={cn(
                'rounded-full px-2 py-0.5 text-[11px] ring-1 transition-colors',
                mono && 'font-mono',
                on ? 'bg-sky-600 text-white ring-sky-600' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50',
              )}
            >
              {o.label}
            </button>
          );
        })}
        {selected.length === 0 && <span className="text-[10px] text-slate-400">any</span>}
      </div>
    </div>
  );
}
